"use client";

import { ReactNode } from "react";
import { useTranslations } from "next-intl";
import {
  ExclamationTriangleIcon,
  FaceFrownIcon,
  MegaphoneIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";
import { Heading, Text, Button } from "@components/theming";

interface Props {
  heading: string | ReactNode;
  subheading?: string | ReactNode;
  bodyText?: string | ReactNode;
  showLinks?: boolean;
  reduceIcons?: boolean;
  reset?: () => void;
}

export default function ErrorPage({
  heading,
  subheading,
  bodyText,
  showLinks = false,
  reduceIcons = false,
  reset,
}: Props) {
  const isShopsite = useIsShopsite();
  const t = useTranslations("Utils");

  return (
    <div
      data-testid="notfound"
      className={joinClassNames(
        "flex items-center h-full p-16",
        isShopsite ? "bg-white rounded-2xl" : ""
      )}
    >
      <div className="container flex flex-col items-center justify-center px-5 mx-auto my-8">
        <div className="max-w-md flex flex-col text-center">
          <div className="relative">
            <Heading
              as="h2"
              sharedClasses="mb-8 relative z-10 text-9xl"
              whitelabelClasses="text-[color:var(--accent-9)] font-black"
              shopsiteClasses="text-indigo-500 font-extrabold"
            >
              {heading}
            </Heading>
            <ExclamationTriangleIcon
              className={joinClassNames(
                "absolute -top-24 left-0 sm:-top-16 sm:-left-16 w-44 h-44 -rotate-12",
                isShopsite ? "text-red-400" : "text-[color:var(--red-9)]"
              )}
            />
            {!reduceIcons && (
              <XMarkIcon
                className={joinClassNames(
                  "absolute -top-8 right-6 sm:-top-6 sm:-right-4 text-red-400 w-8 h-8 -rotate-6 stroke-[4]",
                  isShopsite ? "text-red-400" : "text-[color:var(--red-9)]"
                )}
              />
            )}
          </div>
          <div className="relative">
            {subheading && (
              <Text
                as="p"
                size={{ initial: "6", sm: "7" }}
                weight="bold"
                sharedClasses="relative z-10"
                shopsiteClasses="text-2xl md:text-3xl font-semibold text-black"
              >
                {subheading}
              </Text>
            )}
            {bodyText && (
              <Text
                as="p"
                sharedClasses="relative z-10 mt-4 mb-8"
                shopsiteClasses="text-gray-500"
              >
                {bodyText}
              </Text>
            )}
            <FaceFrownIcon
              className={joinClassNames(
                "absolute -bottom-10 -right-2 sm:-right-[4.25rem] w-24 h-24 rotate-[15deg]",
                isShopsite ? "text-[#F9CF47]" : "text-[color:var(--yellow-a6)]"
              )}
            />
            {!reduceIcons && (
              <MegaphoneIcon
                className={joinClassNames(
                  "absolute -bottom-8 left-2 sm:top-16 sm:left-16 w-12 h-12 rotate-12",
                  isShopsite ? "text-red-400" : "text-[color:var(--red-9)]"
                )}
              />
            )}
          </div>
          {reset && (
            <Button
              size="3"
              onClick={() => reset()}
              sharedClasses="px-8 mt-4 self-center font-semibold"
            >
              {t("tryagain")}
            </Button>
          )}
          {isShopsite && showLinks && (
            <>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <Button href={"/"} shopsiteClasses="px-8">
                  {t("backtohomepage")}
                </Button>
                <Button
                  href={"/contact"}
                  shopsiteVariant="ghost"
                  shopsiteClasses="text-sm gap-1"
                >
                  {t("contact")}
                  <span aria-hidden="true">&rarr;</span>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
